import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  UserCredential,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User as AppUser, UserRole as AppUserRole } from '../utils/types';

// استخدام نوع المستخدم من ملف types.ts
export type User = AppUser;
export type UserRole = AppUserRole;

// التحقق من توفر خدمات Firebase
const ensureFirebaseServices = () => {
  if (!auth) {
    throw new Error('خدمة المصادقة غير متوفرة');
  }
  if (!db) {
    throw new Error('خدمة Firestore غير متوفرة');
  }
  return { auth, db };
};

// Register a new user
export const registerUser = async (
  email: string,
  password: string,
  displayName: string,
  role: UserRole
): Promise<User> => {
  try {
    // التحقق من توفر خدمات Firebase
    const { auth, db } = ensureFirebaseServices();

    // Create user with Firebase Auth
    const userCredential: UserCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update profile with display name
    await updateProfile(user, { displayName });

    // الحصول على الصلاحيات الافتراضية من خدمة الصلاحيات
    const { getDefaultPermissions } = require('./permissionService');
    const permissions = getDefaultPermissions(role);

    // إعداد بيانات المستخدم
    const userData: User = {
      id: user.uid,
      email: user.email || email,
      name: displayName,
      role,
      branchId: '',
      isActive: true,
      permissions,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(doc(db, 'users', user.uid), userData);

    return userData;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

// Login user
export const loginUser = async (email: string, password: string): Promise<User> => {
  console.log(`Attempting login for user: ${email}`);

  try {
    // Check if Firebase services are available
    const { auth, db } = ensureFirebaseServices();
    console.log('Firebase services are available, attempting sign in...');

    // Try to authenticate the user
    let userCredential;
    try {
      userCredential = await signInWithEmailAndPassword(auth, email, password);
    } catch (authError: any) {
      console.error('Firebase authentication error:', authError);
      // Rethrow the auth error to be handled by the login page
      throw authError;
    }

    const user = userCredential.user;
    console.log('User authenticated successfully:', user.uid);

    // Get user data from Firestore
    console.log('Fetching user data from Firestore...');

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        console.log('User data retrieved successfully from Firestore');
        return userData;
      } else {
        console.error('User document not found in Firestore');

        // إنشاء مستند مستخدم افتراضي
        const { getDefaultPermissions } = require('./permissionService');
        const defaultPermissions = getDefaultPermissions(AppUserRole.ADMIN);

        const defaultUserData: User = {
          id: user.uid,
          email: user.email || email,
          name: user.displayName || email.split('@')[0],
          role: AppUserRole.ADMIN,
          branchId: '',
          isActive: true,
          permissions: defaultPermissions,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        try {
          console.log('Creating new user document in Firestore...');
          await setDoc(doc(db, 'users', user.uid), defaultUserData);
          console.log('User document created successfully');
          return defaultUserData;
        } catch (createError) {
          console.error('Error creating user document:', createError);
          throw new Error('فشل في إنشاء بيانات المستخدم');
        }
      }
    } catch (firestoreError) {
      console.error('Error accessing Firestore:', firestoreError);

      // إرجاع بيانات المستخدم الأساسية إذا لم نتمكن من الوصول إلى Firestore
      const { getDefaultPermissions } = require('./permissionService');
      const defaultPermissions = getDefaultPermissions(AppUserRole.ADMIN);

      const basicUserData: User = {
        id: user.uid,
        email: user.email || email,
        name: user.displayName || email.split('@')[0],
        role: AppUserRole.ADMIN,
        branchId: '',
        isActive: true,
        permissions: defaultPermissions,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Returning basic user data due to Firestore error');
      return basicUserData;
    }
  } catch (error: any) {
    console.error('Login error:', error);

    // Add more debugging for network errors
    if (error.code === 'auth/network-request-failed') {
      console.error('Network request failed. Check internet connection.');
    }

    throw error;
  }
};

// Login with Google
export const loginWithGoogle = async (): Promise<User> => {
  try {
    // Check if Firebase services are available
    const { auth, db } = ensureFirebaseServices();
    console.log('Firebase services are available, attempting Google sign in...');

    // Create Google provider
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');

    // Sign in with popup
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;
    console.log('User authenticated with Google successfully:', user.uid);

    // Get user data from Firestore
    console.log('Fetching user data from Firestore...');

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        console.log('User data retrieved successfully from Firestore');
        return userData;
      } else {
        console.error('User document not found in Firestore');

        // إنشاء مستند مستخدم افتراضي كعميل
        const { getDefaultPermissions } = require('./permissionService');
        const defaultPermissions = getDefaultPermissions(AppUserRole.CUSTOMER);

        const defaultUserData: User = {
          id: user.uid,
          email: user.email || '',
          name: user.displayName || user.email?.split('@')[0] || 'مستخدم جديد',
          role: AppUserRole.CUSTOMER, // تعيين دور العميل تلقائيًا
          branchId: '',
          isActive: true,
          permissions: defaultPermissions,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        try {
          console.log('Creating new user document in Firestore...');
          await setDoc(doc(db, 'users', user.uid), defaultUserData);
          console.log('User document created successfully');
          return defaultUserData;
        } catch (createError) {
          console.error('Error creating user document:', createError);
          throw new Error('فشل في إنشاء بيانات المستخدم');
        }
      }
    } catch (firestoreError) {
      console.error('Error accessing Firestore:', firestoreError);

      // إرجاع بيانات المستخدم الأساسية إذا لم نتمكن من الوصول إلى Firestore
      const { getDefaultPermissions } = require('./permissionService');
      const defaultPermissions = getDefaultPermissions(AppUserRole.CUSTOMER);

      const basicUserData: User = {
        id: user.uid,
        email: user.email || '',
        name: user.displayName || user.email?.split('@')[0] || 'مستخدم جديد',
        role: AppUserRole.CUSTOMER, // تعيين دور العميل تلقائيًا
        branchId: '',
        isActive: true,
        permissions: defaultPermissions,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Returning basic user data due to Firestore error');
      return basicUserData;
    }
  } catch (error: any) {
    console.error('Google login error:', error);

    // إعادة توجيه الخطأ مع الكود الأصلي للتعامل معه في واجهة المستخدم
    console.error('Google login error code:', error.code);

    if (error.code === 'auth/operation-not-allowed') {
      console.error('المصادقة باستخدام جوجل غير مفعلة في Firebase. يرجى تفعيلها من لوحة التحكم.');
      throw error; // إعادة توجيه الخطأ الأصلي مع الكود
    } else if (error.code === 'auth/popup-closed-by-user') {
      throw error;
    } else if (error.code === 'auth/popup-blocked') {
      throw error;
    } else if (error.code === 'auth/cancelled-popup-request') {
      throw error;
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      throw error;
    } else {
      throw error;
    }
  }
};

// Logout user
export const logoutUser = async (): Promise<void> => {
  try {
    if (!auth) {
      console.error('Auth service is not available');
      return;
    }

    await signOut(auth);
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

// Check if user has specific permission
export const hasPermission = (user: User | null, permission: keyof User['permissions']): boolean => {
  if (!user) return false;
  return user.permissions[permission] || false;
};

// Get current user data
export const getCurrentUserData = async (userId?: string): Promise<User | null> => {
  try {
    if (!auth || !db) {
      console.error('Firebase services are not available');
      return null;
    }

    // If userId is provided, use it; otherwise use the current authenticated user
    const uid = userId || auth.currentUser?.uid;

    if (!uid) return null;

    const userDoc = await getDoc(doc(db, 'users', uid));

    if (userDoc.exists()) {
      return userDoc.data() as User;
    }

    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};